import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { loyaltyAPI } from '../lib/api';
import { toast } from 'sonner';
import { formatPrice } from '../lib/utils';
import { 
  Gift, 
  Award, 
  Star, 
  Percent,
  Plus,
  Edit,
  Trash2,
  Users,
  TrendingUp,
  Sparkles,
  Crown,
  Settings,
  Coins
} from 'lucide-react';

export default function LoyaltySection() {
  const [config, setConfig] = useState({
    enabled: false,
    points_per_euro: 10,
    points_for_referral: 100,
    welcome_bonus: 50
  });
  const [rewards, setRewards] = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);
  const [stats, setStats] = useState({
    total_points_issued: 0,
    total_points_redeemed: 0,
    active_members: 0
  });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingReward, setEditingReward] = useState(null);
  const [rewardForm, setRewardForm] = useState({
    name: '',
    description: '',
    points_required: 100,
    type: 'DISCOUNT',
    value: 10,
    active: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [configRes, rewardsRes, topRes, statsRes] = await Promise.all([
        loyaltyAPI.getConfig().catch(() => ({ data: config })),
        loyaltyAPI.getRewards().catch(() => ({ data: [] })),
        loyaltyAPI.getTopCustomers().catch(() => ({ data: [] })),
        loyaltyAPI.getStats().catch(() => ({ data: stats }))
      ]);
      
      setConfig(configRes.data || config);
      setRewards(rewardsRes.data || []);
      setTopCustomers(topRes.data || []);
      setStats(statsRes.data || stats);
    } catch (error) {
      console.error('Error loading loyalty data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      await loyaltyAPI.updateConfig(config);
      toast.success('Configurazione salvata');
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleSaveReward = async () => {
    try {
      if (editingReward) {
        await loyaltyAPI.updateReward(editingReward.id, rewardForm);
        toast.success('Premio aggiornato');
      } else {
        await loyaltyAPI.createReward(rewardForm);
        toast.success('Premio creato');
      }
      setDialogOpen(false);
      setEditingReward(null);
      setRewardForm({
        name: '',
        description: '',
        points_required: 100,
        type: 'DISCOUNT',
        value: 10,
        active: true
      });
      loadData();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleDeleteReward = async (id) => {
    if (!confirm('Sei sicuro di voler eliminare questo premio?')) return;
    try {
      await loyaltyAPI.deleteReward(id);
      toast.success('Premio eliminato');
      loadData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const openEditDialog = (reward) => {
    setEditingReward(reward);
    setRewardForm({
      name: reward.name,
      description: reward.description || '',
      points_required: reward.points_required,
      type: reward.type,
      value: reward.value,
      active: reward.active
    });
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Crown className="w-6 h-6 text-primary" />
            Programma Fedeltà
          </h2>
          <p className="text-muted-foreground">Gestisci punti e premi per i tuoi clienti</p>
        </div>
        <Badge variant={config.enabled ? 'default' : 'secondary'} className="h-8 px-4">
          {config.enabled ? 'Attivo' : 'Disattivato'}
        </Badge>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-primary/10">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Membri Attivi</p>
                <p className="text-2xl font-bold">{stats.active_members}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-yellow-500/10">
                <Coins className="w-6 h-6 text-yellow-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Punti Emessi</p>
                <p className="text-2xl font-bold">{stats.total_points_issued?.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-green-500/10">
                <Gift className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Punti Riscattati</p>
                <p className="text-2xl font-bold">{stats.total_points_redeemed?.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-purple-500/10">
                <TrendingUp className="w-6 h-6 text-purple-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">In Circolazione</p>
                <p className="text-2xl font-bold">
                  {(stats.total_points_issued - stats.total_points_redeemed)?.toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="config" className="space-y-4">
        <TabsList>
          <TabsTrigger value="config">
            <Settings className="w-4 h-4 mr-2" />
            Configurazione
          </TabsTrigger>
          <TabsTrigger value="rewards">
            <Gift className="w-4 h-4 mr-2" />
            Premi
          </TabsTrigger>
          <TabsTrigger value="leaderboard">
            <Award className="w-4 h-4 mr-2" />
            Classifica
          </TabsTrigger>
        </TabsList>

        {/* Config Tab */}
        <TabsContent value="config">
          <Card>
            <CardHeader>
              <CardTitle>Impostazioni Programma</CardTitle>
              <CardDescription>Configura come i clienti guadagnano e spendono i punti</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 rounded-lg border">
                <div className="space-y-0.5">
                  <Label className="text-base">Programma Attivo</Label>
                  <p className="text-sm text-muted-foreground">
                    Abilita o disabilita il programma fedeltà
                  </p>
                </div>
                <Switch
                  checked={config.enabled}
                  onCheckedChange={(enabled) => setConfig({ ...config, enabled })}
                />
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label>Punti per Euro speso</Label>
                  <Input
                    type="number"
                    value={config.points_per_euro}
                    onChange={(e) => setConfig({ ...config, points_per_euro: parseInt(e.target.value) || 0 })}
                  />
                  <p className="text-xs text-muted-foreground">
                    Es: 10 punti = 0.10€ di sconto
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Bonus Benvenuto</Label>
                  <Input
                    type="number"
                    value={config.welcome_bonus}
                    onChange={(e) => setConfig({ ...config, welcome_bonus: parseInt(e.target.value) || 0 })}
                  />
                  <p className="text-xs text-muted-foreground">
                    Punti regalo alla registrazione
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Punti Referral</Label>
                  <Input
                    type="number"
                    value={config.points_for_referral}
                    onChange={(e) => setConfig({ ...config, points_for_referral: parseInt(e.target.value) || 0 })}
                  />
                  <p className="text-xs text-muted-foreground">
                    Punti per ogni amico invitato
                  </p>
                </div>
              </div>

              <Button onClick={handleSaveConfig}>
                Salva Configurazione
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Rewards Tab */}
        <TabsContent value="rewards">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Catalogo Premi</CardTitle>
                <CardDescription>Premi riscattabili con i punti fedeltà</CardDescription>
              </div>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button onClick={() => {
                    setEditingReward(null);
                    setRewardForm({
                      name: '',
                      description: '',
                      points_required: 100,
                      type: 'DISCOUNT',
                      value: 10,
                      active: true
                    });
                  }}>
                    <Plus className="w-4 h-4 mr-2" />
                    Nuovo Premio
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>
                      {editingReward ? 'Modifica Premio' : 'Nuovo Premio'}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Nome Premio</Label>
                      <Input
                        value={rewardForm.name}
                        onChange={(e) => setRewardForm({ ...rewardForm, name: e.target.value })}
                        placeholder="Es: Sconto 10%"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Descrizione</Label>
                      <Input
                        value={rewardForm.description}
                        onChange={(e) => setRewardForm({ ...rewardForm, description: e.target.value })}
                        placeholder="Es: Sconto del 10% sul prossimo servizio"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Punti Richiesti</Label>
                        <Input
                          type="number"
                          value={rewardForm.points_required}
                          onChange={(e) => setRewardForm({ ...rewardForm, points_required: parseInt(e.target.value) || 0 })}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Valore (%/€)</Label>
                        <Input
                          type="number"
                          value={rewardForm.value}
                          onChange={(e) => setRewardForm({ ...rewardForm, value: parseInt(e.target.value) || 0 })}
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <Label>Premio Attivo</Label>
                      <Switch
                        checked={rewardForm.active}
                        onCheckedChange={(active) => setRewardForm({ ...rewardForm, active })}
                      />
                    </div>

                    <Button onClick={handleSaveReward} className="w-full">
                      {editingReward ? 'Salva Modifiche' : 'Crea Premio'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {rewards.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Gift className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Nessun premio configurato</p>
                  <p className="text-sm">Crea il tuo primo premio per i clienti fedeli</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {rewards.map((reward) => (
                    <Card key={reward.id} className={`relative ${!reward.active ? 'opacity-60' : ''}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="p-2 rounded-lg bg-primary/10">
                            {reward.type === 'DISCOUNT' ? (
                              <Percent className="w-5 h-5 text-primary" />
                            ) : reward.type === 'FREE_SERVICE' ? (
                              <Sparkles className="w-5 h-5 text-primary" />
                            ) : (
                              <Gift className="w-5 h-5 text-primary" />
                            )}
                          </div>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" onClick={() => openEditDialog(reward)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDeleteReward(reward.id)}>
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                        <h4 className="font-semibold">{reward.name}</h4>
                        {reward.description && (
                          <p className="text-sm text-muted-foreground mt-1">{reward.description}</p>
                        )}
                        <div className="flex items-center justify-between mt-4">
                          <Badge variant="outline" className="gap-1">
                            <Star className="w-3 h-3" />
                            {reward.points_required} punti
                          </Badge>
                          <span className="text-sm font-medium text-primary">
                            {reward.type === 'DISCOUNT' ? `${reward.value}%` : `€${reward.value}`}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Leaderboard Tab */}
        <TabsContent value="leaderboard">
          <Card>
            <CardHeader>
              <CardTitle>Top Clienti Fedeli</CardTitle>
              <CardDescription>I clienti con più punti accumulati</CardDescription>
            </CardHeader>
            <CardContent>
              {topCustomers.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Award className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Nessun cliente nel programma fedeltà</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {topCustomers.map((customer, index) => (
                    <div
                      key={customer.id}
                      className="flex items-center gap-4 p-4 rounded-lg border"
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                        index === 0 ? 'bg-yellow-500 text-white' :
                        index === 1 ? 'bg-gray-400 text-white' :
                        index === 2 ? 'bg-amber-700 text-white' :
                        'bg-muted'
                      }`}>
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{customer.name}</p>
                        <p className="text-sm text-muted-foreground">{customer.email}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-primary">{customer.points?.toLocaleString()} pts</p>
                        <p className="text-xs text-muted-foreground">
                          {customer.total_spent ? formatPrice(customer.total_spent) + ' spesi' : ''}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
